import React, { useState, useEffect } from 'react';
import { Bookmark, Plus, Loader2, Heart, Folder, CheckCircle2, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ActivityCard from '../components/ActivityCard';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Saved = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('saved');
  const [savedActivities, setSavedActivities] = useState([]);
  const [beenThereActivities, setBeenThereActivities] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDesc, setNewListDesc] = useState('');
  const [newListPublic, setNewListPublic] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [deletingList, setDeletingList] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [savedRes, listsRes, btRes] = await Promise.all([
        axios.get(`${API}/api/saves`, { withCredentials: true }),
        axios.get(`${API}/api/lists`, { withCredentials: true }),
        axios.get(`${API}/api/users/been-there`, { withCredentials: true })
      ]);
      setSavedActivities(savedRes.data.activities || []);
      setLists(listsRes.data || []);
      setBeenThereActivities(btRes.data.activities || []);
    } catch (error) {
      console.error('Error fetching saved data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    if (!newListName.trim()) return;
    setCreatingList(true);
    try {
      const response = await axios.post(`${API}/api/lists`, {
        name: newListName.trim(),
        description: newListDesc.trim(),
        is_public: newListPublic
      }, { withCredentials: true });
      setLists(prev => [...prev, response.data]);
      setNewListName('');
      setNewListDesc('');
      setNewListPublic(false);
      setShowCreateList(false);
    } catch (error) {
      console.error('Error creating list:', error);
    } finally {
      setCreatingList(false);
    }
  };

  const handleDeleteList = async (listId) => {
    setDeletingList(listId);
    try {
      await axios.delete(`${API}/api/lists/${listId}`, { withCredentials: true });
      setLists(prev => prev.filter(l => l.id !== listId));
    } catch (error) {
      console.error('Error deleting list:', error);
    } finally {
      setDeletingList(null);
    }
  };

  const handleSaveToggle = (activityId, saved) => {
    if (!saved) setSavedActivities(prev => prev.filter(a => a.id !== activityId));
  };

  if (!user) return null;

  const tabs = [
    { id: 'saved', label: t('saved_tab_saved'), icon: Heart, count: savedActivities.length },
    { id: 'been_there', label: t('saved_tab_been_there'), icon: CheckCircle2, count: beenThereActivities.length },
    { id: 'lists', label: t('saved_tab_lists'), icon: Folder, count: lists.length },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      <header className="sticky top-0 z-40 glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-[#2C302E] font-['Outfit'] mb-4">{t('saved_title')}</h1>
          <div className="flex gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-pill flex items-center gap-2 ${activeTab === tab.id ? 'tab-pill-active' : 'tab-pill-inactive'}`}
                data-testid={`${tab.id}-tab`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : activeTab === 'saved' ? (
          savedActivities.length > 0 ? (
            <div className="grid gap-4">
              {savedActivities.map((activity) => (
                <ActivityCard key={activity.id} activity={activity} variant="compact" onSaveToggle={handleSaveToggle} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[#E07A5F]" />
              </div>
              <h2 className="text-xl font-semibold text-[#2C302E] mb-2">{t('saved_empty_title')}</h2>
              <p className="text-[#666B67] mb-6">{t('saved_empty_desc')}</p>
              <Button onClick={() => navigate('/discover')} className="btn-primary">{t('saved_discover_btn')}</Button>
            </div>
          )
        ) : activeTab === 'been_there' ? (
          beenThereActivities.length > 0 ? (
            <div className="grid gap-4">
              {beenThereActivities.map((activity) => (
                <ActivityCard key={activity.id} activity={activity} variant="compact" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-[#81B29A]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#81B29A]" />
              </div>
              <h2 className="text-xl font-semibold text-[#2C302E] mb-2">{t('been_there_empty_title')}</h2>
              <p className="text-[#666B67] mb-6">{t('been_there_empty_desc')}</p>
              <Button onClick={() => navigate('/discover')} className="btn-primary">{t('saved_discover_btn')}</Button>
            </div>
          )
        ) : (
          <div>
            {/* Create List */}
            <Dialog open={showCreateList} onOpenChange={setShowCreateList}>
              <DialogTrigger asChild>
                <button className="w-full p-4 border-2 border-dashed border-[#2C302E]/20 rounded-2xl flex items-center justify-center gap-2 text-[#666B67] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors mb-6" data-testid="create-list-btn">
                  <Plus className="w-5 h-5" />
                  {t('saved_create_list')}
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('saved_create_list')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateList} className="space-y-4">
                  <Input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder={t('saved_list_name_placeholder')}
                    className="input-field"
                    autoFocus
                    data-testid="new-list-name-input"
                  />
                  <Textarea
                    value={newListDesc}
                    onChange={(e) => setNewListDesc(e.target.value)}
                    placeholder={t('saved_list_desc_placeholder')}
                    className="min-h-[60px]"
                    data-testid="new-list-desc-input"
                  />
                  <label className="flex items-center gap-2 text-sm text-[#666B67] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newListPublic}
                      onChange={(e) => setNewListPublic(e.target.checked)}
                      className="rounded border-[#2C302E]/20"
                    />
                    {t('saved_list_public')}
                  </label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreateList(false)} className="flex-1">
                      {t('common_cancel')}
                    </Button>
                    <Button type="submit" disabled={creatingList || !newListName.trim()} className="flex-1 btn-primary" data-testid="create-list-submit">
                      {creatingList ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common_create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Lists */}
            {lists.length > 0 ? (
              <div className="grid gap-3">
                {lists.map((list) => (
                  <div key={list.id} className="bg-white rounded-2xl flex items-center gap-4 overflow-hidden" data-testid={`list-${list.id}`}>
                    <button
                      onClick={() => navigate(`/list/${list.id}`)}
                      className="flex-1 p-4 flex items-center gap-4 text-left hover:bg-[#F9F8F6] transition-colors"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#81B29A]/10 flex items-center justify-center flex-shrink-0">
                        <Folder className="w-6 h-6 text-[#81B29A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#2C302E]">{list.name}</h3>
                        {list.description && <p className="text-xs text-[#666B67] line-clamp-1">{list.description}</p>}
                        <p className="text-sm text-[#666B67]">{list.activity_ids?.length || 0} {t('saved_activities')}</p>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDeleteList(list.id)}
                      disabled={deletingList === list.id}
                      className="p-4 text-[#666B67] hover:text-red-500 transition-colors"
                      data-testid={`delete-list-${list.id}`}
                    >
                      {deletingList === list.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-[#666B67]">{t('saved_lists_empty')}</p>
              </div>
            )}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Saved;
